import { useState, useEffect } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Plus,
  Edit,
  Trash2,
  Package,
  Settings,
  Bot,
  Loader2,
} from "lucide-react";
import { PageHeader } from "@/components/layout/PageHeader";
import { useToast } from "@/hooks/use-toast";
import {
  listProducts,
  deleteProduct,
  listProductStatusTypes,
  type Product,
} from "@/services/productsService";
import { ProductForm } from "./ProductForm";
import { usePermissions } from "@/hooks/usePermissions";
import { PermissionGuard } from "@/components/auth/PermissionGuard";

interface ProductManagementProps {
  onBack: () => void;
}

type ViewMode = "list" | "form";

export const ProductManagement = ({ onBack }: ProductManagementProps) => {
  const [products, setProducts] = useState<Product[]>([]);
  const [viewMode, setViewMode] = useState<ViewMode>("list");
  const [editingProduct, setEditingProduct] = useState<Product | null>(null);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();
  const { canCreate, canUpdate, canDelete } = usePermissions();

  useEffect(() => {
    loadProducts();
  }, []);

  const loadProducts = async () => {
    setLoading(true);
    try {
      const productsRes = await listProducts({ limit: 100 });

      if (productsRes.success && productsRes.data) {
        setProducts(productsRes.data.products);
      }
    } catch (error) {
      toast({
        title: "Erro ao carregar especialistas",
        description:
          error instanceof Error ? error.message : "Erro desconhecido",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSaveProduct = async () => {
    await loadProducts();
    setViewMode("list");
    setEditingProduct(null);
  };

  const handleNewProduct = () => {
    setEditingProduct(null);
    setViewMode("form");
  };

  const handleEdit = (product: Product) => {
    setEditingProduct(product);
    setViewMode("form");
  };

  const handleDelete = async (productId: string) => {
    if (!confirm("Tem certeza que deseja remover este especialista?")) {
      return;
    }

    try {
      const response = await deleteProduct(productId);

      if (response.success) {
        setProducts(products.filter((p) => p.id !== productId));
        toast({
          title: "Sucesso",
          description: "Especialista removido com sucesso",
        });
        await loadProducts();
      } else {
        throw new Error(response.error?.message || "Erro ao remover especialista");
      }
    } catch (error) {
      toast({
        title: "Erro",
        description:
          error instanceof Error ? error.message : "Erro ao remover especialista",
        variant: "destructive",
      });
    }
  };


  const translateStatus = (statusName?: string) => {
    if (!statusName) return "Desconhecido";
    const key = statusName.toLowerCase().trim();
    const map: Record<string, string> = {
      active: "Ativo",
      inactive: "Inativo",
      development: "Em desenvolvimento",
      deprecated: "Descontinuado",
    };
    const translated = map[key] || statusName;
    return translated.charAt(0).toUpperCase() + translated.slice(1);
  };

  if (viewMode === "form") {
    return (
      <ProductForm
        onBack={() => setViewMode("list")}
        onSave={handleSaveProduct}
        editingProduct={editingProduct}
      />
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-background to-muted flex items-center justify-center">
        <div className="flex flex-col items-center space-y-4">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <p className="text-muted-foreground">Carregando especialistas...</p>
        </div>
      </div>
    );
  }

  return (
    <PermissionGuard
      permission="res_products.read"
      fallback={
        <div className="min-h-screen flex items-center justify-center p-8">
          <Card className="max-w-md w-full">
            <CardHeader>
              <CardTitle>Acesso negado</CardTitle>
              <CardDescription>Você não tem permissão para visualizar especialistas.</CardDescription>
            </CardHeader>
          </Card>
        </div>
      }
    >
    <div className="min-h-screen bg-gradient-to-br from-background to-muted">
      <PageHeader
        icon={Package}
        title="Gerenciamento de Especialistas"
        description="Configure especialistas integrando modelos e agentes"
        onBack={onBack}
        actions={
          canCreate("products") && (
            <Button
              className="bg-gradient-primary text-white hover:opacity-90"
              onClick={handleNewProduct}
            >
              <Plus className="h-4 w-4 mr-2" />
              Novo Especialista
            </Button>
          )
        }
      />

      {/* Main Content */}
      <main className="container mx-auto p-6 space-y-6">
        {/* Stats Cards */}
        <div className="grid gap-4 md:grid-cols-3">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">
                Total de Especialistas
              </CardTitle>
              <Package className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{products.length}</div>
              <p className="text-xs text-muted-foreground">
                especialistas configurados
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">
                Especialistas Ativos
              </CardTitle>
              <Settings className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {products.filter((p) => p.status?.id === 1).length}
              </div>
              <p className="text-xs text-muted-foreground">em funcionamento</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Integrações</CardTitle>
              <Bot className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {products.reduce(
                  (total, p) => total + (p.clients?.length || 0),
                  0
                )}
              </div>
              <p className="text-xs text-muted-foreground">
                associações com clientes
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Products Table */}
        <Card>
          <CardHeader>
            <CardTitle>Lista de Especialistas</CardTitle>
            <CardDescription>
              Gerencie os especialistas e suas integrações com modelos de IA e
              agentes
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Especialista</TableHead>
                  <TableHead>Modelo de IA</TableHead>
                  <TableHead>Agente</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Criado em</TableHead>
                  <TableHead className="text-right">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {products.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={6}
                      className="text-center py-8 text-muted-foreground"
                    >
                      Nenhum especialista cadastrado. Clique em "Novo Especialista" para
                      começar.
                    </TableCell>
                  </TableRow>
                ) : (
                  products.map((product) => (
                    <TableRow key={product.id}>
                      <TableCell>
                        <div>
                          <div className="font-medium">{product.name}</div>
                          {product.description && (
                            <div className="text-sm text-muted-foreground line-clamp-2">
                              {product.description}
                            </div>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center">
                          <Settings className="h-4 w-4 mr-2 text-primary" />
                          {product.model_name}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center">
                          <Bot className="h-4 w-4 mr-2 text-primary" />
                          {product.agent_name}
                          {product.agent_type_name && (
                            <span className="text-muted-foreground ml-1">
                              ({product.agent_type_name})
                            </span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge
                          variant={
                            product.status?.id === 1 ? "default" : "secondary"
                          }
                        >
                          {translateStatus(product.status?.name || product.status_name)}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {new Date(product.created_at).toLocaleDateString(
                          "pt-BR"
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end space-x-2">
                          {canUpdate("products") && (
                            <Button
                              variant="outline"
                              size="icon"
                              onClick={() => handleEdit(product)}
                              className="hover:bg-primary/10"
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                          )}
                          {canDelete("products") && (
                            <Button
                              variant="outline"
                              size="icon"
                              onClick={() => handleDelete(product.id)}
                              className="hover:bg-destructive/10 hover:text-destructive"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </main>
    </div>
    </PermissionGuard>
  );
};
